import { createContext, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FeeModel } from "./loans/fee.model";
import { AccountReceivableModel } from "./loans/account-receivable.model";
import { v4 as uuidv4 } from "uuid";
import { useLoanQuery } from "modules/loan/queries/use-loan-query";
import { LoanContextType } from "./loans/types/loan-context-type";
import { loanContextEmpty } from "./loans/defaults/loan-context-empty";
import { useLoanBalance } from "./loans/hooks/use-loan-balance";

const LoanContext = createContext<LoanContextType>(loanContextEmpty);
const LoanConsumer = LoanContext.Consumer;

function LoanProvider({ children }: { children: JSX.Element }) {
  const { id } = useParams();

  const { data: loan, isFetching: isFetchingLoan, refetch: refetchLoan } = useLoanQuery(id ?? "");

  const {
    data: balance,
    isFetching: isFetchingBalance,
    refetch: refetchBalance,
  } = useLoanBalance(loan);

  const [drawRequest, setDrawRequest] = useState<any>();
  const [accountsReceivables, setAccountsReceivables] = useState<AccountReceivableModel[]>([]);
  const [fees, setFees] = useState<FeeModel[]>([]);
  const [cacheKey, setCacheKey] = useState<string>("");

  useEffect(() => {
    if (loan) {
      setAccountsReceivables(loan.accounts_receivable);
      setFees(loan.fees);
      setCacheKey(uuidv4());
    }
  }, [loan]);

  return (
    <LoanContext.Provider
      value={{
        cacheKey,
        loan,
        refetch: async () => {
          await refetchLoan();
          await refetchBalance();
        },
        balance,
        drawRequest,
        setDrawRequest,
        accountsReceivables,
        setAccountsReceivables,
        fees,
        setFees,
        isFetching: isFetchingLoan || isFetchingBalance,
      }}
    >
      {children}
    </LoanContext.Provider>
  );
}

export function useLoan() {
  return useContext(LoanContext);
}

export { LoanContext, LoanConsumer, LoanProvider };
